import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import '../styles/admin-common.css';

const AchievementManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<any[]>([]);
  const [creatingAchievement, setCreatingAchievement] = useState<any>(null);
  const [editingAchievement, setEditingAchievement] = useState<any>(null);
  const [achievementLogs, setAchievementLogs] = useState<any>(null);
  const [isGeneratingLogs, setIsGeneratingLogs] = useState(false);
  const [loading, setLoading] = useState(false);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadAchievements = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/achievement/admin/list');
      if (response.ok) {
        const result = await response.json();
        setAchievements(result.data);
      }
    } catch (error) {
      console.error('Failed to load achievements:', error);
    }
    setLoading(false);
  };

  const createAchievement = async (achievementData: any) => {
    try {
      // undefined 값 제거
      const cleanData = {
        name: achievementData.name || '',
        description: achievementData.description || '',
        type: achievementData.type || 'EXAM_COUNT',
        condition: {
          type: achievementData.condition?.type || achievementData.type || 'EXAM_COUNT',
          target_value: achievementData.condition?.target_value || 1
        },
        reward_points: achievementData.reward_points || 10,
        icon: achievementData.icon || '🏆',
        is_active: achievementData.is_active !== false
      };
      
      console.log('Sending achievement data:', cleanData);
      const response = await apiCall('/achievement/admin/create', {
        method: 'POST',
        body: JSON.stringify(cleanData),
      });
      if (response.ok) {
        setCreatingAchievement(null);
        loadAchievements();
        alert('업적이 생성되었습니다.');
      } else {
        const error = await response.json();
        alert(error.message || '업적 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to create achievement:', error);
      alert('업적 생성 중 오류가 발생했습니다.');
    }
  };

  const updateAchievement = async (achievementId: string, updates: any) => {
    try {
      const response = await apiCall(`/achievement/admin/${achievementId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (response.ok) {
        setEditingAchievement(null);
        loadAchievements();
        alert('업적이 수정되었습니다.');
      }
    } catch (error) {
      console.error('Failed to update achievement:', error);
      alert('업적 수정에 실패했습니다.');
    }
  };

  const deleteAchievement = async (achievementId: string) => {
    if (!confirm('정말 이 업적을 삭제하시겠습니까?')) return;

    try {
      const response = await apiCall(`/achievement/admin/${achievementId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        loadAchievements();
        alert('업적이 삭제되었습니다.');
      }
    } catch (error) {
      console.error('Failed to delete achievement:', error);
      alert('업적 삭제에 실패했습니다.');
    }
  };

  const generateAchievementLogs = async (dryRun: boolean = true) => {
    setIsGeneratingLogs(true);
    try {
      const response = await apiCall('/achievement/admin/generate-logs', {
        method: 'POST',
        body: JSON.stringify({ dryRun }),
      });
      if (response.ok) {
        const result = await response.json();
        setAchievementLogs(result.data);
        if (!dryRun) {
          alert(`업적 로그 생성 완료!\n처리된 사용자: ${result.data.processedUsers}개\n생성된 업적: ${result.data.totalAchievementsGenerated}개`);
        }
      } else {
        const error = await response.json();
        alert(error.message || '업적 로그 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to generate achievement logs:', error);
      alert('업적 로그 생성 중 오류가 발생했습니다.');
    }
    setIsGeneratingLogs(false);
  };

  useEffect(() => {
    loadAchievements();
  }, []);

  if (!user?.is_admin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>업적 관리</h1>
      </div>

      {loading && <div className="loading">로딩 중...</div>}

      <div className="admin-section">
        <div className="admin-header">
          <h2>업적 관리</h2>
          <div className="achievement-controls">
            <button
              className="admin-btn success"
              onClick={() => setCreatingAchievement({
                name: '',
                description: '',
                type: 'EXAM_COUNT',
                condition: { type: 'EXAM_COUNT', target_value: 1 },
                reward_points: 10,
                icon: '',
                is_active: true
              })}
            >
              업적 추가
            </button>
            <button
              className="admin-btn outline"
              onClick={() => generateAchievementLogs(true)}
              disabled={isGeneratingLogs}
            >
              {isGeneratingLogs ? '분석 중...' : '미리보기'}
            </button>
            <button
              className="admin-btn warning"
              onClick={() => {
                if (confirm('기존 사용자 데이터를 기반으로 업적을 생성하시겠습니까?')) {
                  generateAchievementLogs(false);
                }
              }}
              disabled={isGeneratingLogs}
            >
              업적 로그 생성
            </button>
          </div>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>아이콘</th>
              <th>이름</th>
              <th>설명</th>
              <th>유형</th>
              <th>목표</th>
              <th>포인트</th>
              <th>상태</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {achievements.map(achievement => (
              <tr key={achievement.achievement_id}>
                <td>{achievement.icon || '🏆'}</td>
                <td>{achievement.name}</td>
                <td className="admin-text-sm">{achievement.description}</td>
                <td>
                  <span className="admin-badge info">
                    {achievement.type === 'EXAM_COUNT' ? '시험횟수' :
                      achievement.type === 'CONSECUTIVE_STUDY' ? '연속학습' :
                        achievement.type === 'SCORE_BASED' ? '점수기반' :
                          achievement.type === 'CERTIFICATION_COUNT' ? '자격증개수' : achievement.type}
                  </span>
                </td>
                <td>
                  <span className="admin-badge secondary">{achievement.condition.target_value}</span>
                </td>
                <td>
                  <span className="admin-badge warning">{achievement.reward_points}점</span>
                </td>
                <td>
                  <span className={`admin-badge ${achievement.is_active ? 'success' : 'secondary'}`}>
                    {achievement.is_active ? '활성' : '비활성'}
                  </span>
                </td>
                <td>
                  <div className="admin-flex admin-gap-2">
                    <button className="admin-btn small warning" onClick={() => setEditingAchievement(achievement)}>수정</button>
                    <button className="admin-btn small danger" onClick={() => deleteAchievement(achievement.achievement_id)}>삭제</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {achievementLogs && (
          <div className="achievement-logs-section">
            <h3>업적 로그 분석 결과</h3>
            <div className="logs-summary">
              <p>처리된 사용자: {achievementLogs.processedUsers}명</p>
              <p>생성된 업적: {achievementLogs.totalAchievementsGenerated}개</p>
            </div>
            <div className="logs-details">
              {achievementLogs.userDetails.slice(0, 10).map((user: any, index: number) => (
                <div key={index} className="user-achievement-log">
                  <strong>{user.email}</strong>
                  <div className="user-achievements">
                    {user.achievements.map((ach: any, i: number) => (
                      <span key={i} className="achievement-tag">
                        {ach.name} ({new Date(ach.earnedAt).toLocaleDateString()})
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {achievementLogs.userDetails.length > 10 && (
                <p>... 외 {achievementLogs.userDetails.length - 10}명 더</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 업적 생성 모달 */}
      {creatingAchievement && (
        <div className="modal-overlay">
          <div className="admin-modal-content">
            <div className="admin-modal-header">
              <h3>업적 추가</h3>
              <button className="admin-modal-close" onClick={() => setCreatingAchievement(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <AchievementEditor
                achievement={creatingAchievement}
                onChange={setCreatingAchievement}
                isEditing={false}
              />
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-btn primary"
                onClick={() => {
                  if (!creatingAchievement.name || !creatingAchievement.description) {
                    alert('이름과 설명은 필수입니다.');
                    return;
                  }
                  createAchievement(creatingAchievement);
                }}
              >
                생성
              </button>
              <button className="admin-btn secondary" onClick={() => setCreatingAchievement(null)}>취소</button>
            </div>
          </div>
        </div>
      )}

      {/* 업적 수정 모달 */}
      {editingAchievement && (
        <div className="modal-overlay">
          <div className="admin-modal-content">
            <div className="admin-modal-header">
              <h3>업적 수정</h3>
              <button className="admin-modal-close" onClick={() => setEditingAchievement(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <AchievementEditor
                achievement={editingAchievement}
                onChange={setEditingAchievement}
                isEditing={true}
              />
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-btn primary"
                onClick={() => {
                  if (!editingAchievement.name || !editingAchievement.description) {
                    alert('이름과 설명은 필수입니다.');
                    return;
                  }
                  updateAchievement(editingAchievement.achievement_id, editingAchievement);
                }}
              >
                수정
              </button>
              <button className="admin-btn secondary" onClick={() => setEditingAchievement(null)}>취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 업적 편집기 컴포넌트
const AchievementEditor: React.FC<{
  achievement: any;
  onChange: (achievement: any) => void;
  isEditing: boolean;
}> = ({ achievement, onChange, isEditing }) => {
  const updateAchievement = (updates: any) => {
    onChange({ ...achievement, ...updates });
  };

  return (
    <div className="achievement-editor">
      <div className="admin-form-group">
        <label>업적 이름</label>
        <input
          type="text"
          className="admin-form-control"
          value={achievement.name}
          onChange={(e) => updateAchievement({ name: e.target.value })}
          placeholder="예: 첫 걸음"
        />
      </div>

      <div className="admin-form-group">
        <label>설명</label>
        <textarea
          className="admin-form-control textarea"
          value={achievement.description}
          onChange={(e) => updateAchievement({ description: e.target.value })}
          rows={2}
          placeholder="업적에 대한 설명을 입력하세요"
        />
      </div>

      <div className="admin-flex admin-gap-4">
        <div className="admin-form-group">
          <label>업적 유형</label>
          <select
            className="admin-form-control"
            value={achievement.type}
            onChange={(e) => updateAchievement({
              type: e.target.value,
              condition: { ...achievement.condition, type: e.target.value }
            })}
          >
            <option value="EXAM_COUNT">시험 완료 횟수</option>
            <option value="CONSECUTIVE_STUDY">연속 학습일</option>
            <option value="SCORE_BASED">점수 기반</option>
            <option value="CERTIFICATION_COUNT">자격증 개수</option>
          </select>
        </div>

        <div className="admin-form-group">
          <label>목표 값</label>
          <input
            type="number"
            className="admin-form-control"
            value={achievement.condition.target_value}
            onChange={(e) => updateAchievement({
              condition: {
                ...achievement.condition,
                target_value: parseInt(e.target.value)
              }
            })}
          />
        </div>

        <div className="admin-form-group">
          <label>포인트</label>
          <input
            type="number"
            className="admin-form-control"
            value={achievement.reward_points}
            onChange={(e) => updateAchievement({ reward_points: parseInt(e.target.value) })}
          />
        </div>
      </div>

      <div className="admin-form-group">
        <label>아이콘</label>
        <input
          type="text"
          className="admin-form-control"
          value={achievement.icon}
          onChange={(e) => updateAchievement({ icon: e.target.value })}
          placeholder="예: 🎯"
        />
      </div>

      <div className="admin-form-group">
        <label>
          <input
            type="checkbox"
            checked={achievement.is_active}
            onChange={(e) => updateAchievement({ is_active: e.target.checked })}
          />
          활성 상태
        </label>
      </div>
    </div>
  );
};

export default AchievementManagementPage;